import { useCallback } from 'react'
import { toast } from 'sonner'
import { AICommandType, createAICommand, AICommandStatus } from '@/utils/aiCommandUtils'
import type { NodeJavascripterData, CodeInputState } from '../types'

interface UseJavascriptExecutionParams {
  data: NodeJavascripterData
  codeInputState: CodeInputState
  persistedCode: string
  persistedRequirements: string
  persistedContext: string
  isProcessing: boolean
  syncNodeData: (updates: any) => void
  handleStartProcessing: (message?: string) => void
  handleStopProcessing: (message?: string) => void
  handleCodeChange: (value: string) => void
}

/**
 * Hook for managing JavaScript execution logic.
 * Uses standardized AI command utilities for consistent behavior.
 */
export function useJavascriptExecution({
  data,
  codeInputState,
  persistedCode,
  persistedRequirements,
  persistedContext,
  isProcessing,
  syncNodeData,
  handleStartProcessing,
  handleStopProcessing,
  handleCodeChange,
}: UseJavascriptExecutionParams) {
  // Process JavaScript (generate/enhance/debug)
  const startProcessing = useCallback(() => {
    if (isProcessing) return

    handleStartProcessing('Processing JavaScript...')

    // Create standardized AI command
    const javascriptCommand = createAICommand(AICommandType.JAVASCRIPT_COMMAND, {
      command: codeInputState.command,
      code: persistedCode,
      requirements: persistedRequirements,
      context: persistedContext,
    })

    syncNodeData({
      javascriptCommand,
      statusMessage: 'Processing JavaScript...',
    })

    toast.info('Processing', {
      description: `${codeInputState.command}ing JavaScript code. This may take a moment...`,
    })
  }, [
    codeInputState.command,
    persistedCode,
    persistedRequirements,
    persistedContext,
    isProcessing,
    syncNodeData,
    handleStartProcessing
  ])

  // Execute JavaScript code
  const executeJavaScript = useCallback(() => {
    if (isProcessing || !persistedCode.trim()) return

    handleStartProcessing('Executing JavaScript...')

    // Create standardized AI command for execution
    const javascriptCommand = createAICommand(AICommandType.JAVASCRIPT_COMMAND, {
      command: 'Execute', // Special command for execution
      code: persistedCode,
      context: persistedContext,
      requirements: persistedRequirements,
    })

    syncNodeData({
      javascriptCommand,
      statusMessage: 'Executing JavaScript...',
    })

    toast.info('Executing', {
      description: 'Executing JavaScript code. This may take a moment...',
    })
  }, [
    persistedCode,
    persistedContext,
    persistedRequirements,
    isProcessing,
    syncNodeData,
    handleStartProcessing
  ])

  // Accept generated result
  const acceptResult = useCallback(() => {
    if (!data.javascriptCommand?.result) return

    // Update code with the generated result
    handleCodeChange(data.javascriptCommand.result)

    // Create finished command state
    const finishedCommand = createAICommand(AICommandType.JAVASCRIPT_COMMAND, AICommandStatus.FINISHED, {
      code: data.javascriptCommand.result,
      result: undefined,
      error: undefined,
    })

    syncNodeData({
      javascriptCommand: finishedCommand,
      statusMessage: 'Result accepted',
    })

    handleStopProcessing('Result accepted')
    toast.success('Success', {
      description: 'JavaScript code updated successfully!',
    })
  }, [data.javascriptCommand, syncNodeData, handleCodeChange, handleStopProcessing])

  // Reset the processing state
  const resetProcessing = useCallback(() => {
    // Create reset command state
    const resetCommand = createAICommand(AICommandType.JAVASCRIPT_COMMAND, AICommandStatus.NONE, {
      result: undefined,
      error: undefined,
      executionResult: undefined,
    })

    syncNodeData({
      javascriptCommand: resetCommand,
      statusMessage: 'Processing reset',
    })

    handleStopProcessing('Processing reset')
  }, [syncNodeData, handleStopProcessing])

  return {
    startProcessing,
    executeJavaScript,
    acceptResult,
    resetProcessing,
  }
}
